import { useEffect, useState } from 'react';
import type { PublicView } from '@ringmaster/shared';
import { RosterList } from '../ui/RosterList.js';
import { HowToPlayModal } from '../ui/HowToPlay.js';
import { GameSelectScreen } from '../ui/GameSelectScreen.js';
import { BackPill, cornerPillStyle } from '../ui/BackPill.js';
import { RoleGalleryOverlay } from '../ui/RoleGallery.js';
import {
  FixedActionBar,
  fixedPrimaryButtonStyle,
  fixedSecondaryButtonStyle,
} from '../ui/FixedActionBar.js';
import { gameById, suggestGameForCount } from '../assets/manifest.js';
import { modeChoiceById, renderModeDescription } from '../discord/GameSelectScreen.js';
import { InviteCTA } from '../ui/InviteCTA.js';
import { StallNudge } from '../ui/StallNudge.js';
import { troupeCall } from '../ui/troupeCall.js';
import { TroupeCTA } from '../ui/TroupeCTA.js';
import { BiggerShowCTA } from '../ui/BiggerShowCTA.js';
import { FullMoonBanner } from '../ui/FullMoonBanner.js';
import { Button } from '../ui/Button.js';
import { useCurtainPress } from '../ui/useCurtainPress.js';
import { preloadRoleCardArt } from './RoleRevealScreen.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import { useRoom } from '../net/useRoom.js';

const OPEN_PICKER_KEY = 'rm-open-game-picker';
const howToSeenKey = (gameId: string) => `rm-howto-seen:${gameId}`;

/**
 * LobbyScreen — the game tile (same card as the store) + "How to play" pill on
 * the tile + "Waiting for the Ringmaster to begin…" + joined players list. The
 * player sees themselves in the roster (connection dot + "you" tag), so no
 * separate "your seat" plaque is needed.
 *
 * Host-only "Change game" is a top-left back pill (safe-area aware), opening
 * the fullscreen GameSelectScreen — no longer a tile-attached text link.
 *
 * `onStart` (optional) marks this device as the seated HOST — used by the
 * Discord Activity's mobile/narrow surface, which has no separate /host stage:
 * the waiting pulse becomes a "Raise the curtain" action pinned to the bottom.
 */
export function LobbyScreen({
  publicView,
  myName,
  myId,
  onStart,
  onKick,
}: {
  publicView: PublicView;
  myName: string;
  /** This player's id — hides the kick button on the host's own row. */
  myId?: string;
  onStart?: () => void;
  /** Host-only: kick a player (renders RosterList's per-row remove button). */
  onKick?: (playerId: string) => void;
}) {
  const gameId = publicView.gameId ?? 'bloodmoon';
  const isWhisper = gameId === 'whisper';
  const isHost = !!onStart;
  const send = useRoom((s) => s.send);

  const [howToOpen, setHowToOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Everyone gets the role gallery, host or not — a lone player waiting for
  // friends has nothing else to read, and the cards are public information.
  const [rolesOpen, setRolesOpen] = useState(false);
  // Same title/description as the two-card chooser; a legacy room on a game
  // outside the two modes (e.g. The Whisper) falls back to the manifest.
  const modeChoice = modeChoiceById(gameId);
  const fallbackGame = modeChoice ? null : (gameById(gameId) ?? gameById('bloodmoon'));
  const modeTitle = modeChoice?.title ?? fallbackGame?.name ?? 'Blood Moon';
  const modeDesc = modeChoice?.description ?? fallbackGame?.subtitle ?? null;
  const living = publicView.players.filter((p) => p.alive);
  const canStart = living.length >= publicView.minPlayers;
  // Press behaviour (shake now, narrated refusal throttled) is shared with the
  // desktop lobby — see ui/useCurtainPress.ts.
  const { shake, press } = useCurtainPress(canStart, onStart);
  // Growth affordances (see the invite/nudge notes in InviteCTA + manifest):
  //  · `lonely` — a single seated player has literally nothing else to do, so the
  //    invite becomes the PRIMARY action instead of a disabled start button.
  //  · `fit` — another shipped game whose player band already contains this
  //    crowd; the host gets a one-tap switch, everyone else a passive hint.
  const lonely = living.length <= 1;
  const fit = canStart ? null : suggestGameForCount(living.length, gameId);
  // The AI-seat offer, host-only and only while the table is short. Below the
  // floor it TAKES the curtain button's slot (owner, PR #45).
  const troupe = isHost && !canStart ? troupeCall(gameId, living.length, publicView.minPlayers, send) : null;

  // Warm the role-card art while waiting so the DEAL flip is instant.
  useEffect(() => {
    preloadRoleCardArt();
  }, []);

  // Host-only: auto-open the game picker once, when the END screen's
  // "Change game" button armed it before dispatching PLAY_AGAIN.
  useEffect(() => {
    if (!isHost) return;
    if (sessionStorage.getItem(OPEN_PICKER_KEY) === '1') {
      sessionStorage.removeItem(OPEN_PICKER_KEY);
      setPickerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost]);

  // Auto-show "How to play" once per game, the first time a player reaches
  // this lobby for it.
  useEffect(() => {
    const key = howToSeenKey(gameId);
    if (!localStorage.getItem(key)) {
      setHowToOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  function dismissHowTo() {
    localStorage.setItem(howToSeenKey(gameId), '1');
    setHowToOpen(false);
  }

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: isWhisper
          ? 'linear-gradient(180deg, #000000 0%, var(--arcana-0, #160b26) 100%)'
          : 'linear-gradient(180deg, #000000 0%, #1a0d05 100%)',
        color: 'var(--text-primary)',
        padding: 'var(--space-6) var(--space-5)',
        paddingTop: 'max(var(--space-8), env(safe-area-inset-top, 0px) + var(--space-6))',
        overflow: 'hidden',
      }}
    >
      {/* A quiet wash rising while the table gathers — gold dust for Blood
          Moon, violet arcana wisps for The Whisper. */}
      <ParticleLayer preset={isWhisper ? 'arcanaWisps' : 'goldDust'} intensity={0.4} slot={0} />

      {/* Host-only "Change game" — top-left back pill (replaces the old
          tile-attached text link), safe-area aware. "How to play" (everyone)
          sits top-right, now that it's no longer a tile-attached action. */}
      {isHost && (
        <BackPill
          label="Change game"
          onClick={() => setPickerOpen(true)}
          style={{
            position: 'fixed',
            top: 'max(16px, env(safe-area-inset-top, 0px))',
            left: 'max(16px, env(safe-area-inset-left, 0px))',
            zIndex: 10,
          }}
        />
      )}
      <button
        type="button"
        className="rm-hover"
        onClick={() => setHowToOpen(true)}
        style={{
          ...cornerPillStyle,
          position: 'fixed',
          top: 'max(16px, env(safe-area-inset-top, 0px))',
          right: 'max(16px, env(safe-area-inset-right, 0px))',
          zIndex: 10,
        }}
      >
        How to play
      </button>

      {/* Room code */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 'var(--space-5)' }}>
        <div
          style={{
            display: 'inline-block',
            background: isWhisper ? 'rgba(139, 92, 246, 0.2)' : 'rgba(126, 92, 68, 0.25)',
            borderRadius: 'var(--radius-pill)',
            padding: '6px 14px',
          }}
        >
          <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Room {publicView.code}
          </span>
        </div>
        {/* Full Moon Friday, one line — client-side calendar truth only (a dev
            FULL_MOON_FORCE on the server deliberately does not reach here). */}
        <FullMoonBanner gameId={gameId} variant="phone" />
      </div>

      {/* The mode's name + description — same copy as the two-card chooser,
          no thumbnail. Every player sees it and sees it change live when the
          host switches. */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 'var(--space-5)' }}>
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(26px, 8vw, 34px)',
            letterSpacing: '0.03em',
            // Same two metals as the chooser frames — gold Classic, silver
            // One-night — so the lobby continues the card you picked.
            color: modeChoice?.metal === 'silver' ? '#C9CBCE' : '#E6B866',
          }}
        >
          {modeTitle}
        </h1>
        {modeDesc && (
          <p
            style={{
              margin: '8px auto 0',
              maxWidth: 340,
              fontFamily: 'var(--font-ui)',
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--text-secondary)',
            }}
          >
            {modeChoice
              ? renderModeDescription(modeChoice.description, modeChoice.descriptionEmphasis)
              : modeDesc}
          </p>
        )}
        {/* Something to DO while the seats fill: read the deck. Same corner-pill
            skin as "How to play", but in the content flow rather than pinned. */}
        <button
          type="button"
          className="rm-hover"
          onClick={() => setRolesOpen(true)}
          style={{ ...cornerPillStyle, marginTop: 'var(--space-3)' }}
        >
          View roles
        </button>
      </div>

      {/* Waiting message with subtle pulse (host sees the start CTA instead). */}
      {!onStart && (
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 'var(--space-6)' }}>
          <p
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 17,
              letterSpacing: '0.04em',
              color: 'var(--text-secondary)',
              margin: 0,
              animation: 'hand-pulse 2.8s ease-in-out infinite',
            }}
          >
            The Ringmaster prepares&hellip;
          </p>
          {/* Anyone waiting can pull a friend in — the room fills faster when it
              isn't only the host's job. SWITCH_GAME is host-only, so a non-host
              gets the fitting-game line as a passive hint, never a dead button. */}
          <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', alignItems: 'center' }}>
            <InviteCTA code={publicView.code} emphasis={lonely ? 'primary' : 'secondary'} maxWidth={300} />
            {fit && (
              <p style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-tertiary)', margin: 0, textAlign: 'center' }}>
                {fit.name} plays with {living.length} — ask the Ringmaster to switch.
              </p>
            )}
          </div>
        </div>
      )}

      {/* The two halves of the same idea, and never both at once: under the
          floor, the summons below; big enough for a bigger game, this offer.
          Host-only and once per room — see ui/BiggerShowCTA.tsx. */}
      <BiggerShowCTA
        code={publicView.code}
        gameId={gameId}
        playerCount={living.length}
        isHost={isHost}
        maxWidth={340}
        style={{ position: 'relative', zIndex: 1, margin: '0 auto var(--space-5)' }}
      />

      {/* 60s continuously under the floor → the quiet summons. The voiced
          NEED_PLAYERS beat (server-side, 90s) lands through the normal
          narration path; this is its visual companion. */}
      <StallNudge
        code={publicView.code}
        playerCount={living.length}
        minPlayers={publicView.minPlayers}
        style={{ position: 'relative', zIndex: 1, marginBottom: 'var(--space-5)' }}
        hideLine={troupe != null}
      />

      {/* Player list (shared roster component) — plain Inter, per spec. */}
      {/* Reserve room for however many buttons the fixed bar stacked (start +
          invite + optional game-switch), so the roster never hides under it. */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1, paddingBottom: onStart ? 96 + (fit ? 60 : 0) + 60 : 0 }}>
        <RosterList
          players={publicView.players}
          minPlayers={publicView.minPlayers}
          myName={myName}
          myId={myId}
          onKick={onKick}
        />
      </div>

      {/* Host start CTA — pinned so it never scrolls away under the roster. */}
      {onStart && (
        <FixedActionBar>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {/* A lone host can't start anything — inviting IS the action, so it
                sits above the curtain button and carries the weight. */}
            {lonely && <InviteCTA code={publicView.code} emphasis="primary" />}
            {/* Not `disabled` below the floor — a disabled button eats the tap,
                and the refusal should be AUDIBLE: the engine rejects START_GAME
                without changing state and emits NEED_PLAYERS, so the Ringmaster
                explains. The shake is the local half of the same answer, and
                useCurtainPress throttles the narrated half so hammering the
                button can't stack lines. Mirrors desktop LobbyContent.tsx. */}
            {/* Weight follows usefulness (issue #29, and the owner on PR #39:
                "the link gets lost next to all the primary buttons"). Below the
                floor the curtain cannot open, so it steps down to secondary and
                the invite above/below it is the surface's single primary; at the
                floor the two swap back. The button stays PRESENT and clickable
                either way — that is what keeps the refusal audible. */}
            {troupe ? (
              /* Below the floor the curtain cannot open, so the offer that CAN
                 start a game takes its place (owner, PR #45). The voiced
                 NEED_PLAYERS beat still explains a stalled lobby. */
              <TroupeCTA call={troupe} buttonStyle={fixedSecondaryButtonStyle} />
            ) : (
              <button
                type="button"
                className={`rm-hover-pill${shake ? ' lobby-shake' : ''}`}
                aria-disabled={!canStart}
                onClick={press}
                style={canStart ? fixedPrimaryButtonStyle : fixedSecondaryButtonStyle}
              >
                {canStart
                  ? 'Raise the curtain'
                  : `Need ${publicView.minPlayers - living.length} more player${publicView.minPlayers - living.length === 1 ? '' : 's'}`}
              </button>
            )}
            {/* One tap out of an unfillable lobby — the same host-gated
                SWITCH_GAME the "Change game" picker sends, so seats are kept. */}
            {fit && (
              <Button variant="secondary" onClick={() => send({ type: 'SWITCH_GAME', gameId: fit.id })}>
                Play {fit.name} instead — {living.length} is enough
              </Button>
            )}
            {!lonely && (
              <InviteCTA code={publicView.code} emphasis={canStart ? 'secondary' : 'primary'} />
            )}
          </div>
        </FixedActionBar>
      )}

      {howToOpen && <HowToPlayModal gameId={gameId} onClose={dismissHowTo} />}
      {rolesOpen && (
        <RoleGalleryOverlay
          gameId={gameId}
          playerCount={living.length}
          onClose={() => setRolesOpen(false)}
        />
      )}
      {isHost && pickerOpen && (
        <GameSelectScreen
          currentGameId={gameId}
          onPick={(id) => send({ type: 'SWITCH_GAME', gameId: id })}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <style>{`
        @keyframes lobby-shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-7px); }
          30% { transform: translateX(6px); }
          45% { transform: translateX(-4px); }
          60% { transform: translateX(3px); }
          80% { transform: translateX(-1px); }
        }
        .lobby-shake { animation: lobby-shake 0.46s var(--ease-standard) both; }
        @media (prefers-reduced-motion: reduce) {
          /* The narrator still answers; only the movement goes. */
          .lobby-shake { animation: none !important; }
        }
        @keyframes hand-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="hand-pulse"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
